// @ts-nocheck
import { useCallback, useEffect, useRef, useState } from "react";
import { invokeEdgeFunction } from "@/lib/invokeEdgeFunction";

export interface DivisionNavRow {
  division: string;
  teams: number;
  pools: number;
  pool_games: number;
  pool_games_placed: number;
  pool_games_completed: number;
  bracket_games: number;
  bracket_games_placed: number;
  restrictions: number;
  restrictions_pending: number;
  places: number;
  color: string | null;
  allowed_courts: number;
  gender: string | null;
  max_teams: number | null;
  entry_fee: number | null;
}

/**
 * Live counts for the divisions sidebar.
 *
 * Nothing here is cached between visits: a stale "Teams (12)" after a team
 * withdrew is worse than no count at all. Every mount, every refresh and every
 * save re-reads the numbers from the database.
 */
export function useDivisionNav(eventId?: string) {
  const [rows, setRows] = useState<DivisionNavRow[]>([]);
  const [event, setEvent] = useState<any>(null);
  const [versionId, setVersionId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Only the very first read blanks the screen. A background refresh (window
  // focus, a save elsewhere) must never unmount the screen underneath someone
  // mid-edit — that is how a dialog vanishes and a click never lands.
  const loadedOnce = useRef(false);

  const load = useCallback(async () => {
    if (!eventId) return;
    if (!loadedOnce.current) setLoading(true);
    const { data, error: e } = await invokeEdgeFunction("rle-operator-division-nav", {
      body: { event_id: eventId },
    });
    if (e || !data?.ok) {
      setError(data?.error?.message ?? "Could not load the divisions");
    } else {
      setError(null);
      setRows(data.data.divisions ?? []);
      setEvent(data.data.event ?? null);
      setVersionId(data.data.version_id ?? null);
    }
    loadedOnce.current = true;
    setLoading(false);
  }, [eventId]);

  useEffect(() => { void load(); }, [load]);

  // Counts go stale the moment another screen writes: refresh on return.
  useEffect(() => {
    const onFocus = () => { if (document.visibilityState === "visible") void load(); };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onFocus);
    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onFocus);
    };
  }, [load]);

  return { rows, event, versionId, loading, error, refresh: load };
}
